import { __assign } from "tslib";
import { EventEmitter } from 'eventemitter3';
import { AUDIO_EVENTS, PLAYBACK_EVENTS, STATE_EVENTS } from '../constants';
import AudioEvents from './audio-events';
import StateEvents from './state-events';
export var defaultAudioProps = {
    muted: false,
    volume: 1,
    playbackRate: 1,
    loop: false,
    preload: 'auto',
    autoplay: false
};
var awaitEvent = function (eventEmitter, eventName) {
    return new Promise(function (resolve) {
        eventEmitter.once(eventName, function () { return resolve(); });
    });
};
/**
 * Playback instances ties actual audio element, track properties and event
 * handling together.
 * @doc Playback
 */
var Playback = /** @class */ (function () {
    function Playback() {
        this._audioProps = defaultAudioProps;
        this._playerId = '';
        this._playlist = [];
        this._trackIndex = 0;
        // @ts-ignore
        this._eventEmitter = new EventEmitter();
        this._audioElement = new Audio();
        this._stateEvents = new StateEvents(this._eventEmitter, this._audioElement);
        this._audioEvents = new AudioEvents(this._eventEmitter, this._audioElement);
    }
    /**
     * Removes audio element, all event listeners and calls [`reset()`](#reset)
     */
    Playback.prototype.destroy = function () {
        this._eventEmitter.removeAllListeners();
        this._stateEvents.destroy();
        this._audioEvents.destroy();
        this.reset();
        delete this._audioElement;
    };
    /**
     * Removes audio src, empties playlist
     */
    Playback.prototype.reset = function () {
        this._playerId = '';
        this._playlist = [];
        this._trackIndex = 0;
        this._audioElement.pause();
        this._audioElement.src = '';
    };
    /**
     * Register player with provided id and fires
     * `playback-events/player-changed` event.
     */
    Playback.prototype.registerPlayer = function (playerId) {
        this._playerId = playerId;
        this._eventEmitter.emit(PLAYBACK_EVENTS.PLAYER_CHANGED, playerId);
    };
    /**
     * Returns true if [`registerPlayer`](#registerPlayer) has
     * been called and player id exists.
     */
    Playback.prototype.hasRegisteredPlayer = function () {
        return Boolean(this._playerId);
    };
    Playback.prototype.getPlayerId = function () {
        return this._playerId;
    };
    Playback.prototype.getPlaylist = function () {
        return this._playlist;
    };
    /**
     * Sets provided playlist and fires `playback-events/playlist-changed` event.
     */
    Playback.prototype.setPlaylist = function (playlist) {
        this._playlist = playlist;
        this.setTrackIndex(0);
        this._eventEmitter.emit(PLAYBACK_EVENTS.PLAYLIST_CHANGED, playlist);
    };
    /**
     * Track index represents current track in playlist.
     */
    Playback.prototype.getTrackIndex = function () {
        return this._trackIndex;
    };
    /**
     * Sets track index (if not provided, sets to 0) and fires two events:
     * `playback-events/track-changed` and `playback-state/src-set`.
     * It will pause player.
     */
    Playback.prototype.setTrackIndex = function (trackIndex) {
        if (trackIndex === void 0) { trackIndex = 0; }
        var track = this._playlist[trackIndex];
        if (!track) {
            return;
        }
        this._audioElement.pause();
        this._audioElement.src = track.url;
        this._audioElement.volume = this._audioProps.volume;
        this._audioElement.muted = this._audioProps.muted;
        this._audioElement.playbackRate = this._audioProps.playbackRate;
        this._audioElement.loop = this._audioProps.loop;
        this._audioElement.preload = this._audioProps.preload;
        this._audioElement.autoplay = this._audioProps.autoplay;
        this._trackIndex = trackIndex;
        this._stateEvents.setState(STATE_EVENTS.SRC_SET);
        this._eventEmitter.emit(PLAYBACK_EVENTS.TRACK_CHANGED, trackIndex);
    };
    Playback.prototype.getAudioProps = function () {
        return this._audioProps;
    };
    Playback.prototype.setAudioProps = function (audioProps) {
        // @TODO reset to default when set?
        this._audioProps = __assign(__assign({}, this._audioProps), audioProps);
    };
    /**
     * Mutes volume and fires `audio-events/mute-changed` event.
     */
    Playback.prototype.setMute = function (muted) {
        this._audioProps.muted = muted;
        this._audioElement.muted = muted;
        // @TODO check current muted state
        return awaitEvent(this._eventEmitter, AUDIO_EVENTS.MUTE_CHANGED);
    };
    /**
     * Sets volume in percentage and fires `audio-events/volume-changed` event.
     */
    Playback.prototype.setVolume = function (volume) {
        this._audioProps.volume = volume;
        this._audioElement.volume = volume;
        return awaitEvent(this._eventEmitter, AUDIO_EVENTS.VOLUME_CHANGED);
    };
    /**
     * Sets playback rate and fires `audio-events/rate-changed` event.
     */
    Playback.prototype.setPlaybackRate = function (playbackRate) {
        this._audioProps.playbackRate = playbackRate;
        this._audioElement.playbackRate = playbackRate;
        return awaitEvent(this._eventEmitter, AUDIO_EVENTS.RATE_CHANGED);
    };
    Playback.prototype.setLoop = function (loop) {
        this._audioProps.loop = loop;
        this._audioElement.loop = loop;
    };
    Playback.prototype.setAutoplay = function (autoplay) {
        this._audioProps.autoplay = autoplay;
        this._audioElement.autoplay = autoplay;
    };
    /**
     * Starts/resumes playing and fires `playback-state/play-requested` event.
     */
    Playback.prototype.play = function () {
        var playPromise = this._audioElement.play();
        if (playPromise !== undefined) {
            return playPromise;
        }
        return awaitEvent(this._eventEmitter, STATE_EVENTS.PLAY_REQUESTED);
    };
    /**
     * Pauses and fires `playback-state/paused` event.
     */
    Playback.prototype.pause = function () {
        this._audioElement.pause();
        return awaitEvent(this._eventEmitter, STATE_EVENTS.PAUSED);
    };
    /**
     * Moves track playing position and fires `audio-events/time-updated` event.
     */
    Playback.prototype.seekTo = function (value) {
        this._audioElement.currentTime = value;
        return awaitEvent(this._eventEmitter, AUDIO_EVENTS.TIME_UPDATED);
    };
    /**
     * Skip current track and play next one in playlist.
     */
    Playback.prototype.next = function () {
        if (this._playlist.length < 1) {
            return;
        }
        var nextIndex = this._trackIndex + 1;
        // Cycle through playlist
        if (nextIndex >= this._playlist.length) {
            nextIndex = 0;
        }
        this.setTrackIndex(nextIndex);
    };
    /**
     * Skip current track and play previous one in playlist.
     */
    Playback.prototype.previous = function () {
        if (this._playlist.length < 1) {
            return;
        }
        var prevIndex = this._trackIndex - 1;
        // Cycle through playlist
        if (prevIndex < 0) {
            prevIndex = this._playlist.length - 1;
        }
        this.setTrackIndex(prevIndex);
    };
    Playback.prototype.on = function (event, fn, context) {
        return this._eventEmitter.on(event, fn, context);
    };
    Playback.prototype.once = function (event, fn, context) {
        return this._eventEmitter.once(event, fn, context);
    };
    Playback.prototype.off = function (event, fn, context, once) {
        return this._eventEmitter.off(event, fn, context, once);
    };
    return Playback;
}());
export default Playback;
//# sourceMappingURL=playback.js.map